/*
 * _customled-quickview-enhancements.js  (Maranello M1.10.x; M2.3 desc-persist fix 2026-07-04)
 *
 * Augments Maranello's native <quick-view-drawer> (vendor file
 * assets/quick-view-drawer.js) with two enhancements:
 *
 *   1. Truncated product description excerpt - mirroring the Activ8 LIVE
 *      quickview pattern. Patches `getProductInfoHtml`.
 *
 *   2. Ship-time block injection near the ATC button + countdown timer
 *      enhancement on variant change. Patches `getProductFormHtml` (which
 *      Maranello calls on every variant change in the quickview, so the
 *      ship-time message stays in sync with whichever variant the
 *      customer has picked inside the drawer).
 *
 * Both patches are implemented as JS prototype patches so no vendor file
 * is modified.
 *
 * Upgrade-proof:
 *   - Owned file, prefixed with `_customled-`.
 *   - Patches the class registered via customElements.define(); waits for
 *     definition before patching.
 *   - If Maranello renames the drawer class or restructures the patched
 *     methods, the patches silently no-op (won't crash; the drawer just
 *     renders without our enhancements).
 *
 * Loaded from snippets/_customled-stylesheets.liquid, gated to product
 * templates for now (the drawer is currently only rendered on PDPs by
 * our owned loader). Move the load point to global if/when the drawer
 * is enabled site-wide.
 */
(function () {
  "use strict";

  var ATTEMPTS = 0;
  var MAX_ATTEMPTS = 50; // 50 * 60ms = 3s upper bound

  function tryPatch() {
    ATTEMPTS++;
    var klass =
      window.customElements && customElements.get("quick-view-drawer");
    if (!klass) {
      if (ATTEMPTS < MAX_ATTEMPTS) {
        setTimeout(tryPatch, 60);
      }
      return;
    }
    if (klass.__customledEnhancementsPatched) return;
    klass.__customledEnhancementsPatched = true;

    patchGetProductInfoHtml(klass);
    patchGetProductFormHtml(klass);
    patchHandleUpdateProduct(klass);
  }

  // Shared: build + insert the description excerpt into a `.product-info-main`
  // container, before the view-more link if present (else appended). No-ops if
  // the container already has one, or the excerpt is empty. Used by BOTH the
  // initial-render patch and the variant-change patch.
  function insertDescriptionExcerpt(productInfo, excerpt) {
    if (!productInfo || !excerpt) return;
    if (productInfo.querySelector(".product-quick-view-description")) return;

    var descEl = document.createElement("div");
    descEl.className = "product-quick-view-description rte";
    descEl.textContent = excerpt;

    var viewMore = productInfo.querySelector(".product-info-view-more");
    if (viewMore) {
      productInfo.insertBefore(descEl, viewMore);
    } else {
      productInfo.appendChild(descEl);
    }
  }

  // Install (once per drawer instance) a MutationObserver that keeps the
  // description excerpt present in `.product-info-main`. The vendor rebuilds
  // that region on every in-drawer variant change (multiple code paths, some
  // async), which wipes our injected excerpt; getProductInfoHtml only runs on
  // initial open. The observer re-inserts the cached excerpt whenever the
  // region reappears without it - path-, timing-, and future-proof. The
  // insert is idempotent (helper no-ops when a description is already present),
  // so it can't loop on its own mutation. Same defensive pattern as the
  // dealer-banner cart-note injector.
  function ensureDescriptionObserver(drawer) {
    if (!drawer || drawer.__customledDescObserver) return;

    var obs = new MutationObserver(function () {
      var productInfo = drawer.querySelector(".product-info-main");
      if (!productInfo) return;

      // If the current product's description is showing, it IS the source of
      // truth - cache its text. This seeds/refreshes the cache from the live
      // DOM (correct for whichever product is open) instead of the unreliable
      // `html` argument to getProductInfoHtml, which the drawer sometimes calls
      // with payloads that omit the description source.
      var live = productInfo.querySelector(".product-quick-view-description");
      if (live) {
        var txt = (live.textContent || "").trim();
        if (txt) drawer.__customledDescExcerpt = txt;
        return;
      }

      // Description is missing (a variant-change re-render wiped it) - re-insert
      // the cached excerpt for the current product. insertDescriptionExcerpt is
      // idempotent, so re-inserting can't loop on the observer's own mutation.
      if (drawer.__customledDescExcerpt) {
        insertDescriptionExcerpt(productInfo, drawer.__customledDescExcerpt);
      }
    });
    obs.observe(drawer, { childList: true, subtree: true });
    drawer.__customledDescObserver = obs;
  }

  // -------- getProductInfoHtml patch: description excerpt (initial open) --------
  // On the drawer's first render of a product: inject the truncated description
  // excerpt into `.product-info-main`, cache it on the drawer instance, and arm
  // the persistence observer so it survives later variant changes.
  function patchGetProductInfoHtml(klass) {
    var original = klass.prototype.getProductInfoHtml;
    if (typeof original !== "function") return;

    klass.prototype.getProductInfoHtml = function (html) {
      var baseHtml = original.call(this, html);

      // Arm the persistence observer (idempotent). The observer owns the cached
      // excerpt - seeding it from the live rendered description rather than this
      // method's `html` argument (which is unreliable across the drawer's
      // internal calls). We do NOT cache here, to avoid clobbering a good cache
      // with an empty excerpt when `html` lacks the description source.
      ensureDescriptionObserver(this);

      var excerpt = extractDescriptionExcerpt(html);
      if (!excerpt) return baseHtml;

      var tmp = document.createElement("div");
      tmp.innerHTML = baseHtml;
      var productInfo = tmp.querySelector(".product-info-main");
      if (!productInfo) return baseHtml;

      insertDescriptionExcerpt(productInfo, excerpt);
      return tmp.innerHTML;
    };
  }

  // -------- getProductFormHtml patch: ship-time block --------
  // Why patch the form method instead of product-info:
  //   1. Position: ship-time should sit near the ATC button. The form
  //      output renders into the drawer footer (.quick-view-drawer-footer)
  //      where the ATC lives.
  //   2. Variant sync: Maranello calls `getProductFormHtml` on EVERY
  //      variant change inside the drawer (the form re-renders with the
  //      new variant's price/availability). Patching here means our
  //      extraction + countdown re-runs automatically on each variant
  //      change, keeping the ship-time message in sync with the
  //      currently-selected variant - no PUB_SUB subscription needed
  //      (PUB_SUB doesn't fire for quickview-internal variant changes
  //      anyway; scripts.js short-circuits to handleQuickviewUpdate).
  function patchGetProductFormHtml(klass) {
    var original = klass.prototype.getProductFormHtml;
    if (typeof original !== "function") return;

    klass.prototype.getProductFormHtml = function (html) {
      var baseHtml = original.call(this, html);

      var shipTimeSrc = extractShipTimeBlock(html);
      if (!shipTimeSrc) return baseHtml;

      // Apply countdown enhancement (in-place mutation on the cloned
      // node before serialization). For in_stock state on weekday +
      // before noon ET + non-blocked dates, rewrites the SSR
      // next-day message to the live countdown. Other states pass
      // through unchanged (SSR already correct).
      applyQuickviewCountdown(shipTimeSrc);

      // Wrap the form HTML so we can prepend the ship-time block as a
      // sibling at the top. The drawer footer renders the resulting HTML
      // and the ship-time block ends up directly above the ATC controls
      // (price, variants, ATC button).
      var wrapper = document.createElement("div");
      wrapper.innerHTML = baseHtml;
      wrapper.insertBefore(shipTimeSrc, wrapper.firstChild);
      return wrapper.innerHTML;
    };
  }

  // -------- handleUpdateProduct patch: refresh ship-time on variant change --------
  // Maranello's quickview has two render paths:
  //   - Initial open: renderProduct -> getProductFormHtml (handled by
  //     patchGetProductFormHtml above; our ship-time injection fires).
  //   - Variant change inside drawer: handleUpdateProduct -> updateProductForm
  //     (partial update - only swaps the ATC button and updates classes;
  //     does NOT call getProductFormHtml, so our injection above doesn't
  //     fire on variant changes).
  //
  // Patch handleUpdateProduct to also refresh the ship-time block from the
  // newly-fetched HTML on every variant change. The fetched HTML's SSR
  // already reflects the new variant's state (data-customled-stock-state
  // is variant-aware via the _customled-stock-state resolver), so we
  // extract + countdown-enhance + replace the existing block.
  function patchHandleUpdateProduct(klass) {
    var original = klass.prototype.handleUpdateProduct;
    if (typeof original !== "function") return;

    klass.prototype.handleUpdateProduct = function (html, sectionId, variant) {
      var result = original.call(this, html, sectionId, variant);

      // NOTE: description persistence across variant changes is handled by the
      // MutationObserver installed in patchGetProductInfoHtml (see
      // ensureDescriptionObserver) - NOT here. The vendor re-renders
      // .product-info-main on variant change through more than one path and
      // sometimes asynchronously, so a synchronous re-inject in this method
      // raced the rebuild and dropped on the 2nd+ change. The observer re-adds
      // the cached excerpt whenever the region loses it, path- and timing-proof.

      // Extract the new variant's ship-time block from the fetched HTML
      var newShipTime = extractShipTimeBlock(html);
      if (!newShipTime) return result;

      applyQuickviewCountdown(newShipTime);

      // The drawerFooter holds the form (and our ship-time block from
      // initial render). Replace the existing block; if for some reason
      // it's missing (e.g., initial render skipped extraction), prepend.
      var footer = this.drawerFooter ||
        this.querySelector(".drawer-footer") ||
        this.querySelector(".quick-view-drawer-footer");
      if (!footer) return result;

      var existing = footer.querySelector(".customled-ship-time");
      if (existing) {
        existing.replaceWith(newShipTime);
      } else {
        footer.insertBefore(newShipTime, footer.firstChild);
      }

      return result;
    };
  }

  // Extract the SSR-rendered .customled-ship-time block from the fetched
  // PDP HTML. Returns a cloned DOM node ready to insert, with:
  //   - Inline <script> tags stripped (defense in depth; innerHTML doesn't
  //     execute scripts but we keep the markup clean).
  //   - `id="shipping-status"` stripped from the status element (avoids
  //     duplicate-ID HTML invalidity when both PDP and quickview blocks
  //     coexist on the page; class hook `.customled-ship-time__status`
  //     remains for JS targeting).
  //   - `.customled-ship-time--quickview` modifier class added so CSS
  //     applies the box treatment + hides the CTA tail.
  function extractShipTimeBlock(html) {
    if (!html) return null;
    var src = null;
    if (typeof html.querySelector === "function") {
      src = html.querySelector(".customled-ship-time");
    }
    if (!src && html.ownerDocument) {
      src = html.ownerDocument.querySelector(".customled-ship-time");
    }
    if (!src) return null;

    var clone = src.cloneNode(true);

    // Strip script tags
    var scripts = clone.querySelectorAll("script");
    for (var i = 0; i < scripts.length; i++) {
      scripts[i].parentNode.removeChild(scripts[i]);
    }

    // Strip the #shipping-status ID - PDP keeps it; quickview drops it
    // to stay HTML-valid (single-ID-per-document).
    var statusWithId = clone.querySelector("#shipping-status");
    if (statusWithId) statusWithId.removeAttribute("id");

    clone.classList.add("customled-ship-time--quickview");
    return clone;
  }

  // Apply countdown timer to in_stock state when conditions match.
  // Mirrors the PDP's updateShippingStatus weekday-before-noon branch:
  //
  //   if today is a weekday AND before noon ET AND not a US postal
  //   holiday / merchant blackout date:
  //     show "IN STOCK! Order within X hours and Y minutes to ship today!"
  //   else:
  //     leave the SSR next-day message in place
  //
  // Other states (custom, highdemand, backorder, preorder) pass through
  // unchanged - their SSR rendering is already final, no JS-driven
  // re-formatting needed.
  //
  // Depends on:
  //   - window.CustomLEDShippingCalendar (set by the PDP ship-time
  //     snippet; provides getCurrentEasternTime +
  //     isHolidayOrManualBlackoutDate).
  //   - window.customledShippingI18n (locale strings: hour/hours/minute/
  //     minutes/and/in_stock_today_label_html/in_stock_today_suffix_html).
  //
  // If either global is missing (e.g., quickview opened on a non-PDP page
  // where the ship-time snippet doesn't render), the function silently
  // returns and the SSR next-day message stays.
  function applyQuickviewCountdown(shipTimeBlock) {
    if (!shipTimeBlock) return;
    var state = shipTimeBlock.getAttribute("data-customled-stock-state");
    if (state !== "in_stock") return;

    var i18n = window.customledShippingI18n;
    var cal = window.CustomLEDShippingCalendar;
    if (!i18n || !cal || typeof cal.getCurrentEasternTime !== "function") return;

    var easternNow = cal.getCurrentEasternTime();
    var easternDay = easternNow.getDay();
    var easternHour = easternNow.getHours();
    var easternMinutes = easternNow.getMinutes();
    var isBlockedDay =
      typeof cal.isHolidayOrManualBlackoutDate === "function" &&
      cal.isHolidayOrManualBlackoutDate(easternNow);

    // Outside the countdown window - SSR next-day message stays.
    if (easternDay === 0 || easternDay === 6 || isBlockedDay || easternHour >= 12) {
      return;
    }

    var minutesRemaining = (11 - easternHour) * 60 + (60 - easternMinutes);
    var hoursRemaining = Math.floor(minutesRemaining / 60);
    var remainingMinutes = minutesRemaining % 60;
    var hourWord = hoursRemaining !== 1 ? i18n.hours : i18n.hour;
    var minuteWord = remainingMinutes !== 1 ? i18n.minutes : i18n.minute;

    var statusEl = shipTimeBlock.querySelector(".customled-ship-time__status");
    if (!statusEl) return;

    statusEl.innerHTML =
      i18n.in_stock_today_label_html +
      " <b>" + hoursRemaining + " " + hourWord + "</b> " +
      i18n.and +
      " <b>" + remainingMinutes + " " + minuteWord + "</b> " +
      i18n.in_stock_today_suffix_html;
  }

  // Pull the truncated description from the fetched PDP HTML. The text
  // was server-rendered by Liquid (`product.description | strip_html |
  // truncatewords: 30`) into a hidden element via
  // snippets/_customled-stylesheets.liquid, so we just read it back.
  function extractDescriptionExcerpt(html) {
    if (!html) return "";
    var doc = html.ownerDocument || html;
    if (!doc || typeof doc.querySelector !== "function") return "";
    var src = doc.querySelector("[data-customled-quickview-description-source]");
    if (!src) return "";
    return (src.textContent || "").replace(/\s+/g, " ").trim();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryPatch);
  } else {
    tryPatch();
  }
})();
